import { useContext, useEffect, useState } from 'react'

import api from '../services/api'
import toastError from '../errors/toastError'
import { AuthContext } from '../context/Auth/AuthContext'

let cachedDisableGroupBotValue = null
let disableGroupBotPromise = null

const resolveDisableGroupBotValue = settings => {
  if (!Array.isArray(settings)) {
    return null
  }

  const setting = settings.find(s => s.key === 'disableGroupBot')
  if (!setting) {
    return null
  }

  return setting.value === 'enabled'
}

const useDisableGroupBot = () => {
  const { user } = useContext(AuthContext)

  const [isGroupBotDisabled, setIsGroupBotDisabled] = useState(() => {
    const valueFromUserSettings = resolveDisableGroupBotValue(user?.company?.settings)

    if (valueFromUserSettings !== null) {
      cachedDisableGroupBotValue = valueFromUserSettings
      return valueFromUserSettings
    }

    if (cachedDisableGroupBotValue !== null) {
      return cachedDisableGroupBotValue
    }

    return false
  })

  useEffect(() => {
    let isMounted = true

    const applyValue = value => {
      cachedDisableGroupBotValue = value
      if (isMounted) {
        setIsGroupBotDisabled(value)
      }
    }

    const valueFromUserSettings = resolveDisableGroupBotValue(user?.company?.settings)
    if (valueFromUserSettings !== null) {
      applyValue(valueFromUserSettings)
      return () => {
        isMounted = false
      }
    }

    if (cachedDisableGroupBotValue !== null) {
      if (isMounted) {
        setIsGroupBotDisabled(cachedDisableGroupBotValue)
      }
      return () => {
        isMounted = false
      }
    }

    const loadSetting = async () => {
      try {
        if (!disableGroupBotPromise) {
          disableGroupBotPromise = api.get('/settings/').then(({ data }) => {
            const value = resolveDisableGroupBotValue(data)
            const normalizedValue = value ?? false
            cachedDisableGroupBotValue = normalizedValue
            disableGroupBotPromise = null
            return normalizedValue
          })
        }

        const value = await disableGroupBotPromise
        if (isMounted) {
          setIsGroupBotDisabled(value)
        }
      } catch (err) {
        disableGroupBotPromise = null
        toastError(err)
      }
    }

    loadSetting()

    return () => {
      isMounted = false
    }
  }, [user?.company?.settings])

  return isGroupBotDisabled
}

export default useDisableGroupBot
